import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {Grid, IconButton, useTheme} from "@mui/material";
import {IndentSpace, Title4} from "../../../domains/common/components/Typography";
import {
  FormInput,
  FormInputAddressCtrl,
  FormInputCtrl,
  FormInputDate,
  Textarea,
  TextareaCtrl
} from "../../../domains/common/components/FormInput";
import FormSelect from "../../../domains/common/components/FormSelect";
import {errorsDescription, IDDocumentTypes} from "../../../dictionaries";
import Box from "@mui/material/Box";
import DeleteIcon from "@mui/icons-material/Delete";
import {deleteParticipant, setAddressDataToSubject} from "../participant";
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import {DealActionsEnums, handleDealAction} from "../../../actions";
import {personDataError, validatePersonData} from "../../../validation";
import {ErrorTooltip} from "../../../domains/common/components/Tooltip";
import {FormSelectCountryAlpha3Codes} from "../../../domains/common/components/FormSelectCountry";
import {ButtonBase} from "../../../domains/common/components/Button";
import {dealData} from "../deal";
import {DealPageContext} from "../index";


/**
 * Форма ввода данных для Физического лица
 * @param {Participant} values
 * @param optionElements
 * @param onChange
 * @param onDelete
 * @returns {Element}
 * @constructor
 */
export function PersonForm({values, optionElements, onChange, onDelete}) {
  // Индекс в массиве заявителей dealData.participants
  const [participantIdx, setParticipantIdx] = useState(null);
  const [addr, setAddr] = useState({
    fiasCode: values?.registrationFiasCode,
    fullAddress: values?.registrationFullAddress,
    regionCode: values?.registrationRegionCode
  })
  const [person, setPerson] = useState(values);
  const [deleteButton, setDeleteButton] = useState('');
  const [error, setError] = useState();

  const theme = useTheme();

  useEffect(() => {
    let participantIdx = dealData.participants.findIndex((item) => item.participantId === values.participantId)

    if (
      !dealData.participants.length &&
      !values.participantId &&
      participantIdx === -1
    ) {
      participantIdx = 0;

      dealData.participants.push({
        subjectId: 0,
      })
    }

    setParticipantIdx(participantIdx);
    const participant = dealData.participants[participantIdx]

    validatePersonData(participant)
    setError(personDataError)

    if (onDelete) {
      setDeleteButton(
        <Grid item xs={12} container spacing={1}>
          <Grid item xs={9} style={{height: 20}}></Grid>
          <Grid item xs={3}>
            <ButtonBase variant="contained" startIcon={<DeleteIcon/>} size="small" color="error"
                    onClick={async () => {
                      const result = await deleteParticipant(person?.participantId);
                      if (result.ok) {
                        handleDealAction(DealActionsEnums.DELETE_PARTICIPANT)
                        onDelete ? onDelete() : void 0;
                      }
                    }}>
              Удалить
            </ButtonBase></Grid>
        </Grid>);
    }
  }, []);

  const onSelectAddress = (option) => {
    if (!option) {
      setAddr({fiasCode: '', fullAddress: '', regionCode: ''});
      setAddressDataToSubject({
        participantId: values.participantId,
        addressData: null,
        registrationFullAddress: null,
        registrationFiasCode: null,
        registrationRegionCode: null,
      });
    } else {
      setAddr({fiasCode: option?.id, fullAddress: option?.label, regionCode: option?.regionCode})
      setAddressDataToSubject({
        participantId: values.participantId,
        addressData: option?.address,
        registrationFullAddress: option?.label,
        registrationFiasCode: option?.id,
        registrationRegionCode: option?.regionCode,
      });
    }

    const participant = dealData.participants[participantIdx];
    setError(validatePersonData(participant, 'addressData'))

    onChange ? onChange() : void 0;
  }

  const handleDatePick = (name, val) => {
    onChange ? onChange() : void 0;

    const participant = dealData.participants[participantIdx];
    participant[name] = val;
    setError(validatePersonData(participant, name))
  }

  const handleChange = (e) => {
    onChange ? onChange() : void 0;

    const name = e.target.name;
    const value = e.target.value;

    const participant = dealData.participants[participantIdx];
    participant[name] = value;

    validatePersonData(participant, e.target.name)

    setPerson({...participant});

    setError(personDataError)
  }

  const explodeFIO = (fio) => {
    const vals = fio.split(' ');
    const newVals = {}

    vals[0] ? newVals["lastName"] = vals[0].trim() : void 0,
      vals[1] ? newVals["firstName"] = vals[1].trim() : void 0,
      vals[2] ? newVals["middleName"] = vals[2].trim() : void 0,

      setPerson(Object.assign({...person, ...newVals}));

    const participant = dealData.participants[participantIdx];
    newVals.lastName?.length ? participant["lastName"] = newVals.lastName : void 0;
    newVals.firstName?.length ? participant["firstName"] = newVals.firstName : void 0;
    newVals.middleName?.length ? participant["middleName"] = newVals.middleName : void 0;

    onChange ? onChange() : void 0;
  }

  return (
    <Grid container size={12} spacing={0}>
      <Grid item xs={6}>
        <Grid item xs={12} container spacing={1} alignItems="stretch">
          <Grid item xs={12}><Title4 title="Персональные данные"/></Grid>
          <Grid item xs={12}>
            <FormInputCtrl label="Фамилия" required
                           name="lastName" onChange={handleChange} value={values?.lastName} error={error?.lastName}/>
            {
              error?.lastName ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.PERSON_LAST_NAME}/>
                </> : <>
                  <IndentSpace size={5}/>
                  <IconButton aria-label="delete" size="small" sx={{marginTop: '5px'}} onClick={() => explodeFIO(values?.lastName)}>
                    <ChecklistRtlIcon fontSize="inherit" sx={{color: theme.palette.icon.secondary}}/>
                  </IconButton>
                </>
            }
          </Grid>
          <Grid item xs={12}>
            <FormInputCtrl label="Имя" required
                           name="firstName" onChange={handleChange} value={person?.firstName} error={error?.firstName}/>
            {
              error?.firstName ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.PERSON_FIRST_NAME}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}>
            <FormInputCtrl label="Отчество"
                           name="middleName" onChange={handleChange} value={person?.middleName}
                           error={error?.middleName}/>
            {
              error?.middleName ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.PERSON_MIDDLE_NAME}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}><FormSelect data={[{key: 'male', value: 'муж.'}, {key: 'female', value: 'жен.'}]}
                                         label="Пол"
                                         required
                                         value={person?.gender}
                                         onChange={handleChange}
                                         name="gender"/></Grid>
          <Grid item xs={12}>
            <FormInputDate label="Дата рождения" name="birthDate" required
                           value={person?.birthDate}
                           error={error?.birthDate}
                           onChange={(val) => handleDatePick('birthDate', val)}/>
            {
              error?.birthDate ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.PERSON_BIRTHDATE}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}><Textarea onChange={handleChange} label="Место рождения" rows={4}
                                       multiline={true} name="birthPlace" value={person?.birthPlace}/></Grid>
        </Grid>
        <Grid item xs={12} container spacing={1} alignItems="stretch">
          <Grid item xs={12}></Grid>
          <Grid item xs={12}><Title4 title="Регистрация"/></Grid>
          <Grid item xs={12}>
            <FormSelectCountryAlpha3Codes value={person?.citizenship || 'RUS'} name="citizenship" label='Гражданство *'
                                          onChange={handleChange} required/>
          </Grid>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <FormInputAddressCtrl onSelect={onSelectAddress} defaultValue={addr.fullAddress} label="Адрес регистрации *"
                                  error={error?.registrationFIASAddress}/>
            {
              error?.registrationFIASAddress ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.PERSON_ADDR_REGISTRATION}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12} sx={{height: 40, maxWidth: 240}}><Box
            sx={{fontWeight: 'light', fontSize: 'small'}}>{addr.fullAddress}</Box></Grid>
          <Grid item xs={12}>
            <Textarea onChange={handleChange} label="Адрес регистрации по документам" rows={3}
                      multiline={true} name="manualFullAddress"
                      value={person?.manualFullAddress}/>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6} alignItems="stretch">
        <Grid item xs={12} container spacing={1}>
          <Grid item xs={12}><Title4 title="Документ"/></Grid>
          <Grid item xs={12}>
            <FormInputCtrl label="СНИЛС *" name="snils" value={person?.snils}
                           onChange={handleChange} error={error?.snils}
                           inputProps={{maxLength: 14}}/>
            {
              error?.snils ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.SNILS}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}>
            <FormInput label="ИНН *" name="inn" inputProps={{maxLength: 12}}
                       onChange={handleChange} error={error?.inn}
                       value={person?.inn}/>
            {
              error?.inn ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.INN}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}>
            <FormSelect data={IDDocumentTypes} label="Вид документа *" onChange={handleChange}
                        value={person?.IDDocumentType || 'passport'}
                        name="IDDocumentType"/>
          </Grid>
          {
            ['passport', 'birthCertificate'].includes(person?.IDDocumentType) || !person?.IDDocumentType ?
              <Grid item xs={12}>
                <FormInput label="Серия" name="series" onChange={handleChange}
                                            value={person?.series} error={error?.series}/>
                {
                  error?.series ?
                    <>
                      <IndentSpace size={5}/>
                      <ErrorTooltip placement="bottom" title={errorsDescription.RU_PASSPORT_SERIES}/>
                    </> : void 0
                }
              </Grid>
            : void 0
          }
          <Grid item xs={12}>
            <FormInput label="Номер *" name="number" onChange={handleChange}
                                        value={person?.number} error={error?.number}/>

            {
              error?.number ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.RU_PASSPORT_NUMBER}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}><TextareaCtrl label="Выдан" name="issuer" multiline={true} rows={4}
                                       onChange={handleChange}
                                       fullWidth={250} value={person?.issuer} error={error?.issuer}/>
            {
              error?.issuer ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.RU_PASSPORT_ISSUER}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}><FormInputDate label="Дата выдачи" name="issueDate"
                                            onChange={(val) => handleDatePick('issueDate', val)}
                                            value={person?.issueDate}/></Grid>
          {person?.IDDocumentType === 'passport' || !person?.IDDocumentType ?
          <Grid item xs={12}><FormInput label="Код подразделения" name="divisionCode"
                                        onChange={handleChange}
                                        value={person?.divisionCode}
                                        inputProps={{maxLength: 7}}
                                        error={error?.divisionCode}/>
            {
              error?.divisionCode ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.RU_PASSPORT_DIVISION_CODE}/>
                </> : void 0
            }</Grid>
            : void 0
        }
        </Grid>
        <Grid item xs={12} container spacing={1} sx={{paddingTop: '10px'}}>
          <Grid item xs={12}><Title4 title="Контакты"/></Grid>
          <Grid item xs={12}><FormInput label="Телефон *" name="phone" onChange={handleChange}
                                        value={person?.phone} error={error?.phone}/>
            {
              error?.phone ?
                <>
                  <IndentSpace size={5}/>
                  <ErrorTooltip placement="bottom" title={errorsDescription.PERSON_PHONE}/>
                </> : void 0
            }
          </Grid>
          <Grid item xs={12}><FormInput label="e-mail" name="email" onChange={handleChange}
                                        value={person?.email}/></Grid>
        </Grid>
      </Grid>
      {
        optionElements
      }
      <Grid item xs={9} style={{height: 20}}></Grid>
      {
        deleteButton
      }
    </Grid>
  )
}

